import { AxiosError } from 'axios';
import api from '../api/apiClient';

export type ReviewType = 'stylist-review' | 'client-review';

export interface ReviewRating {
  overall: number;
  aspects: {
    serviceQuality?: number;
    timeManagement?: number;
    communication?: number;
    valueForMoney?: number;
    reliability?: number;
    punctuality?: number;
    courtesy?: number;
  };
}

export interface ReviewContent {
  text: string;
  pros?: string[];
  cons?: string[];
  images?: { url: string; caption?: string }[];
}

export interface StylistReply {
  text: string;
  createdAt: string;
}

export interface StylistAspects {
  serviceQuality: number;
  timeManagement: number;
  communication: number;
  valueForMoney: number;
}

export interface ClientAspects {
  reliability: number;
  punctuality: number;
  courtesy: number;
  communication: number;
}

export interface ReviewFlag {
  user: string;
  reason: 'inappropriate' | 'spam' | 'fake' | 'other';
  description?: string;
  createdAt: string;
}

export interface Review {
  _id: string;
  client: { _id: string; name: string; profilePhoto?: string };
  stylist: { _id: string; name: string; profilePhoto?: string };
  reviewType: ReviewType;
  appointment: string;
  service: {
    stylistService: string;
    name: string;
    variation?: { type: 'hair' | 'size' | 'complexity'; details: any };
  };
  rating: ReviewRating;
  review: ReviewContent;
  stylistReply?: StylistReply;
  status: 'pending' | 'published' | 'hidden' | 'flagged' | 'removed';
  flags?: ReviewFlag[];
  verifiedPurchase: boolean;
  helpfulVotes: { user: string; helpful: boolean; createdAt: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface ReviewAnalytics {
  totalReviews: number;
  averageRating: number;
  aspectAverages: StylistAspects | ClientAspects;
  distribution: Record<number, number>;
}

export interface ReviewsResponse {
  reviews: Array<Review & { viewerIsAuthor?: boolean }>;
  viewerHasReviewed?: boolean;
  pagination: { page: number; limit: number; total: number; pages: number };
  stats: {
    averageRating: number;
    totalReviews: number;
    distribution: Record<number, number>;
    aspectAverages?: StylistAspects | ClientAspects;
  };
}

export interface CreateReviewRequest {
  appointmentId: string;
  reviewType?: ReviewType;
  rating: ReviewRating;
  review?: string;
  pros?: string[];
  cons?: string[];
  images?: { url: string; caption?: string }[];
}

/**
 * Service for handling reviews (stylist & client).
 */
export async function createReview(
  data: CreateReviewRequest
): Promise<Review> {
  try {
    const response = await api.postWithData<{ data: Review }>('/reviews', data);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Create review error:', err.response?.data || err.message);
    throw err;
  }
}

export async function getStylistReviews(
  stylistId: string,
  options?: { page?: number; limit?: number }
): Promise<ReviewsResponse> {
  try {
    const response = await api.getWithParams<{ data: ReviewsResponse }>(
      `/reviews/stylist/${stylistId}`,
      options
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Get stylist reviews error:', err.response?.data || err.message);
    return { reviews: [], pagination: { page: 1, limit: options?.limit || 10, total: 0, pages: 0 }, stats: { averageRating: 0, totalReviews: 0, distribution: {}, aspectAverages: undefined } };
  }
}

export async function getClientReviews(
  clientId: string,
  options?: { page?: number; limit?: number }
): Promise<ReviewsResponse> {
  try {
    const response = await api.getWithParams<{ data: ReviewsResponse }>(
      `/reviews/client/${clientId}`,
      { params: options }
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Get client reviews error:', err.response?.data || err.message);
    return { reviews: [], pagination: { page: 1, limit: options?.limit || 10, total: 0, pages: 0 }, stats: { averageRating: 0, totalReviews: 0, distribution: {}, aspectAverages: undefined } };
  }
}

export async function getAppointmentReview(
  appointmentId: string,
  reviewType?: ReviewType
): Promise<Review | null> {
  try {
    const response = await api.getWithParams<{ data: Review }>(
      `/reviews/appointment/${appointmentId}`,
      { reviewType }
    );
    return response.data.data;
  } catch (error) {
    console.error('Get appointment review error:', error);
    return null;
  }
}

export async function updateReview(
  reviewId: string,
  data: Partial<CreateReviewRequest>
): Promise<Review> {
  try {
    const response = await api.putWithData<{ data: Review }>(
      `/reviews/${reviewId}`,
      data
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Update review error:', err.response?.data || err.message);
    throw err;
  }
}

export async function deleteReview(reviewId: string): Promise<void> {
  try {
    await api.deleteWithParams(`/reviews/${reviewId}`);
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Delete review error:', err.response?.data || err.message);
    throw err;
  }
}

export async function getReviewableAppointments(): Promise<Array<{
  _id: string;
  dateTime: string;
  stylist: { _id: string; name: string; profilePhoto?: { url: string } };
  client: { _id: string; name: string };
  service: { name: string };
}>> {
  try {
    const response = await api.getWithParams<{ data: any[] }>('/reviews/reviewable-appointments', {});
    return response.data.data || [];
  } catch (error) {
    console.error('Get reviewable appointments error:', error);
    return [];
  }
}

export async function replyToReview(
  reviewId: string,
  reply: string
): Promise<Review> {
  try {
    const response = await api.postWithData<{ data: Review }>(
      `/reviews/${reviewId}/reply`,
      { reply }
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Reply to review error:', err.response?.data || err.message);
    throw err;
  }
}

export async function reportReview(
  reviewId: string,
  data: { reason: ReviewFlag['reason']; description?: string }
): Promise<void> {
  try {
    await api.postWithData(`/reviews/${reviewId}/report`, data);
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Report review error:', err.response?.data || err.message);
    throw err;
  }
}

export async function getReviewAnalytics(
  userId: string,
  reviewType: ReviewType = 'stylist-review'
): Promise<ReviewAnalytics> {
  try {
    const response = await api.getWithParams<{ data: ReviewAnalytics }>(
      `/reviews/analytics/${userId}`,
      { type: reviewType }
    );
    return response.data.data;
  } catch (error) {
    console.error('Get review analytics error:', error);
    return {
      totalReviews: 0,
      averageRating: 0,
      aspectAverages: reviewType === 'stylist-review' ? { serviceQuality: 0, timeManagement: 0, communication: 0, valueForMoney: 0 } : { reliability: 0, punctuality: 0, courtesy: 0, communication: 0 },
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    };
  }
}

const reviewService = {
  createReview,
  getStylistReviews,
  getClientReviews,
  getAppointmentReview,
  getReviewableAppointments,
  updateReview,
  deleteReview,
  replyToReview,
  reportReview,
  getReviewAnalytics
};

export default reviewService;
